import { Formik } from 'formik';
import { TeamAsyncSelect, TeamSelectOption } from 'modules/teams/ui/components/team-async-select';
import React, { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';

import { AppDispatch } from 'store/store';
import Modal from 'ud-ui/components/modal';
import UDButton from 'ud-ui/components/button';
import { Player } from 'modules/players/domain/interfaces/player';
import { addTournamentUserToTeam } from 'modules/players/store/actions';

type FormValues = {
  team?: TeamSelectOption;
};

function useModalAddToTeam() {
  const [isOpen, setIsOpen] = useState(false);
  const dispatch = useDispatch<AppDispatch>();

  const [player, setPlayer] = useState<Player>();

  const open = useCallback(() => {
    setIsOpen(true);
  }, []);
  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleClickTeam = useCallback(
    (teamId: number) => {
      if (!player?.id) {
        toast.error('Не удалось определить игрока, перезагрузите страницу и попробуйте снова');
        return;
      }

      if (player.positionsInTeams.some((position) => position?.team?.id === teamId)) {
        toast.warn('Игрок уже состоит в команде');
        return;
      }

      dispatch(addTournamentUserToTeam({ teamId: teamId, userId: player.id }))
        .unwrap()
        .then(() => {
          close();
          toast.success('Игрок добавлен в команду');
        })
        .catch((error) => {
          console.error(error);
          if (error === 'User is already in this team') {
            toast.warn('Игрок уже состоит в команде');
          } else {
            toast.error('Ошибка при добавлении игрока в команду, попробуйте ещё раз или обратитесь в поддержку');
          }
        });
    },
    [close, dispatch, player],
  );

  const handleSubmit = useCallback(
    (values: FormValues) => {
      const { team } = values;
      if (!team?.data?.id) {
        return;
      }

      handleClickTeam(team.data.id);
    },
    [handleClickTeam],
  );

  const Component = useCallback(() => {
    return (
      <Formik<FormValues> initialValues={{ team: undefined }} onSubmit={handleSubmit}>
        {(formProps) => {
          return (
            <Modal show={isOpen} onClose={close} size="md">
              <form onSubmit={formProps.handleSubmit}>
                <Modal.Header title="Добавление в команду" />

                <Modal.Body className={'h-[180px]'}>
                  <TeamAsyncSelect
                    className="w-full"
                    name="team"
                    value={formProps.values.team}
                    onChange={(option) => formProps.setFieldValue('team', option)}
                    onBlur={() => formProps.setFieldTouched('team', true)}
                  />
                </Modal.Body>
                <Modal.Footer>
                  <UDButton
                    type="submit"
                    variant="primary"
                    loading={formProps.isSubmitting}
                    stretch
                    disabled={!formProps.values.team || !formProps.dirty}
                  >
                    Добавить
                  </UDButton>
                </Modal.Footer>
              </form>
            </Modal>
          );
        }}
      </Formik>
    );
  }, [close, handleSubmit, isOpen]);

  const openModal = useCallback(
    (player: Player) => {
      setPlayer(player);

      open();
    },
    [open],
  );

  return { open: openModal, close, Modal: Component };
}

export default useModalAddToTeam;
